import { useState, useEffect } from "react";
import classNames from "classnames";
import { RivetAIMarkLoader } from "components/common/RivetAIMarkLoader";

export enum LoaderMode {
  modal = "modal",
  overlay = "overlay",
  screen = "screen",
  inline = "inline",
}

export enum LoaderTheme {
  default = "default",
  green = "green",
  blue = "blue",
  red = "red",
  brand = "brand",
  indigo = "indigo",
  purple = "purple",
  violet = "violet",
}

interface TLoaderV2Props {
  className?: string;
  mode?: LoaderMode;
  theme?: LoaderTheme;
  size?: "sm" | "md" | "lg";
  /** Overrides sm/md/lg pixel size for the brand mark. */
  markSize?: number;
  rotatingTexts?: string[];
  textInterval?: number; // in milliseconds
  textClassName?: string;
}

const commonModalLoaderStyles =
  "absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full h-full !important z-[9999]";

const modeStyles: Record<LoaderMode, string> = {
  modal: `rounded-xl bg-card ${commonModalLoaderStyles}`,
  overlay: `bg-transparent ${commonModalLoaderStyles}`,
  screen: `w-full h-full`,
  inline: "relative",
};

const themeStyles: Record<LoaderTheme, string> = {
  default: "text-primary",
  green: "text-hue-emerald",
  blue: "text-hue-blue",
  red: "text-danger",
  brand: "text-primary",
  indigo: "text-hue-indigo",
  purple: "text-hue-violet",
  violet: "text-hue-violet",
};

const sizePixels = {
  sm: 28,
  md: 40,
  lg: 72,
} as const;

export const LoaderV2 = ({
  className,
  mode = LoaderMode.modal,
  theme = LoaderTheme.default,
  size = "md",
  markSize,
  rotatingTexts = [],
  textInterval = 1500,
  textClassName = "text-muted-foreground/70 text-xs",
}: TLoaderV2Props) => {
  const [currentTextIndex, setCurrentTextIndex] = useState(0);

  useEffect(() => {
    if (!rotatingTexts || rotatingTexts.length === 0) return;

    const interval = setInterval(() => {
      setCurrentTextIndex((prev) => (prev + 1) % rotatingTexts.length);
    }, textInterval);

    return () => clearInterval(interval);
    // Depend on length, not array identity, to avoid unintended restarts
  }, [rotatingTexts.length, textInterval]);

  return (
    <div className={classNames("flex items-center justify-center", className, modeStyles[mode])}>
      <div className="flex flex-col items-center gap-3">
        <RivetAIMarkLoader
          size={markSize ?? sizePixels[size]}
          className={classNames("transition-all duration-200", themeStyles[theme])}
        />
        {rotatingTexts && rotatingTexts.length > 0 && (
          <p className={textClassName}>{rotatingTexts[currentTextIndex]}</p>
        )}
      </div>
    </div>
  );
};

export default LoaderV2;
